import React from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import "./App.css";
import { isMobile } from "react-device-detect";
import {
  UserAuthContextProvider,
  useUserAuth,
} from "./context/userAuthContext";
import { CurrencyProvider } from "./context/CurrencyContext.js";
import { CartProvider } from "./context/CartContext";
import AlmacenNavbar from "./Navbar.js";
import MobileNavbar from "./MobileNavbar.js";
import AnalyticsTracker from "./AnalyticsTracker.js";
import Home from "./components/Home";
import Login from "./components/Login";
import Signup from "./components/Signup";
import PhoneSignUp from "./components/PhoneSignUp";
import NotAuthorizedScreen from "./screens/NotAuthorizedScreen.jsx";
import UserMenu from "./components/Users/UserMenu";
import UserForm from "./components/Users/UserForm.js";
import NewAdminDashboard from "./components/AdminPanel/NewAdminDashboard";
import StoreEditor from "./components/AdminPanel/StoreEditor";
import AdminLayout from "./components/AdminPanel/AdminLayout";
import Profile from "./components/Profile/Profile";
import SearchFriends from "./components/Friends/SearchFriends";
import FriendRequests from "./components/Users/FriendRequests";
import SearchPanel from "./components/Search/SearchPanel";
import CheckOut from "./components/CheckOut/CheckOut.js";
import Success from "./components/CheckOut/Success";
import Failure from "./components/CheckOut/Failure";
import Pending from "./components/CheckOut/Pending";
import CreateStore from "./components/Stores/CreateStore.js";
import AllStores from "./components/Stores/AllStores.js";
import NewUser from "./components/Users/NewUser.js";
import Wallet from "./components/SpeedCoins/Wallet.js";
import TransactionDetails from "./components/SpeedCoins/TransactionDetails.js";
import MyFriends from "./components/Users/MyFriends.js";
import SendNotification from "./components/SendNotification.js";
import ProductDataFetcher from "./components/store/ProductDataFetcher.js";
import Categorias from "./components/store/categorias/Categorias.js";
import ProductosCategoria from "./components/store/categorias/ProductosCategoria.js";
import Search from "./components/store/productos/Search.js";
import Cart from "./components/store/carrito/Cart.jsx";
import MembershipView from "./components/Memberships/Membership.jsx";
import NewActivity from "./components/Activities/NewActivity";
import Inbox from "./components/Messenger/Inbox.jsx";
import MyActivities from "./components/Activities/MyActivities/MyActivities.jsx";
import NewCategory from "./components/Categories/NewCategory.jsx";
import Feed from "./components/Feed.jsx";
import VineLanding from "./components/LandingPage/VineLanding.jsx";
import RestaurantCategoryUploader from "./components/Restaurants/RestaurantCategoryUploader.jsx";
import RestaurantExplorer from "./components/Restaurants/RestaurantExplorer.jsx";
import RestBack from "./components/Restaurants/RestBack.jsx";
import FoodQuiz from "./components/Restaurants/FoodQuiz/FoodQuiz.jsx";
import NewPlace from "./components/Places/NewPlace.jsx";
import Orders from "./components/Places/Orders.jsx";
import PlaceInfo from "./components/Places/PlaceInfo.jsx";
import UploadMenuComponent from "./components/Importer/UploadMenuComponent.jsx";
import BeerMenuComponent from "./components/Menus/BeerMenuComponent.jsx";
import Dashboard from "./components/Menus/Dashboard.jsx";
import FoodMenuComponent from "./components/Menus/FoodMenuComponent.jsx";
import BeerRoulette from "./components/Menus/BeerRoulette.jsx";
import Sucursales from "./components/Stores/Sucursales.jsx";
import Ordenes from "./components/Stores/Orders/Ordenes.jsx";
import Facturacion from "./components/Facturacion/Facturacion.jsx";
import QR from "./components/Wallet/QR.jsx";
import MainHome from "./components/Home/MainHome.jsx";

// ← IMPORTS MANTENIDOS para el AdminDashboard
import Servicios from "./components/MicroServicios/Servicios.jsx";
import MyPlaces from "./components/Places/myPlaces.jsx";
import ProductManager from "./components/Products/ProductManager.jsx";

// ✅ FIXED: EnhancedProtectedRoute que ESPERA userDataLoading
const EnhancedProtectedRoute = ({ children, role }) => {
  const { user, loading, userDataLoading } = useUserAuth();
  const location = useLocation();

  // 🔑 ESPERAR AMBOS: loading Y userDataLoading
  if (loading || userDataLoading) {
    console.log("⏳ EnhancedProtectedRoute esperando:", {
      loading,
      userDataLoading,
    });
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Verificando permisos...</p>
          <p className="text-sm text-gray-500 mt-1">
            {userDataLoading
              ? "Cargando datos de usuario..."
              : "Iniciando sesión..."}
          </p>
        </div>
      </div>
    );
  }

  // Si no hay usuario
  if (!user) {
    console.log("❌ No hay usuario autenticado");
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  // 🔍 DEBUG: Log completo del usuario
  console.log("🔍 EnhancedProtectedRoute - Verificando:", {
    uid: user.uid,
    email: user.email,
    type: user.type,
    roleRequired: role,
    hasType: !!user.type,
    isAdmin: user.type === "admin",
  });

  // ✅ VERIFICAR ROL o PERMISO
  if (role) {
    // Si se requiere un rol específico, verificar
    if (user.type !== role && user.role !== role) {
      console.log(
        "❌ Acceso DENEGADO - Requiere:",
        role,
        "Tiene:",
        user.type || user.role
      );
      return <Navigate to="/notauthorized" replace />;
    }
  }

  console.log("✅ Acceso CONCEDIDO - Rol:", user.type);
  return children;
};

// ✅ FIXED: AppLayout también espera userDataLoading
const AppLayout = ({ children, requiresAuth = false }) => {
  const { user, loading, userDataLoading } = useUserAuth();
  const location = useLocation();

  // 🔑 Detectar ruta /admin para ocultar navbar
  const isAdminRoute = location.pathname.startsWith("/admin");

  // Esperar tanto loading general como datos del usuario
  if (loading || userDataLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-orange-50 to-white">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 font-medium">🐔 Pollo Feliz</p>
          <p className="text-sm text-gray-500 mt-1">
            {userDataLoading ? "Cargando perfil..." : "Iniciando aplicación..."}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen">
      {/* Navbar OCULTO SOLO en /admin */}
      {!isAdminRoute && <AlmacenNavbar />}

      <main
        className={`flex-1 ${
          isAdminRoute ? "pt-0" : isMobile ? "pt-20 pb-20" : "pt-20"
        }`}
      >
        {children}
      </main>

      {/* Analytics OCULTO en admin */}
      {user && !isAdminRoute && <AnalyticsTracker />}

      {/* MobileNavbar OCULTO en /admin */}
      {!isAdminRoute && <MobileNavbar />}
    </div>
  );
};

// Rutas públicas
const PublicRoute = ({ children }) => {
  return <AppLayout requiresAuth={false}>{children}</AppLayout>;
};

// Rutas privadas
const PrivateRoute = ({ children, role }) => {
  return (
    <AppLayout requiresAuth={true}>
      <EnhancedProtectedRoute role={role}>{children}</EnhancedProtectedRoute>
    </AppLayout>
  );
};

function App() {
  return (
    <UserAuthContextProvider>
      <CurrencyProvider>
        <CartProvider>
          <Routes>
            {/* 🟢 RUTAS PÚBLICAS - CON NAVBAR */}
            <Route
              path="/"
              element={
                <PublicRoute>
                  <MainHome />
                </PublicRoute>
              }
            />
            <Route
              path="/sucursales"
              element={
                <PublicRoute>
                  <Sucursales />
                </PublicRoute>
              }
            />
            <Route
              path="/order/:storeId"
              element={
                <PublicRoute>
                  <Ordenes />
                </PublicRoute>
              }
            />
            <Route
              path="/facturacion"
              element={
                <PublicRoute>
                  <Facturacion />
                </PublicRoute>
              }
            />
            <Route
              path="/menu"
              element={
                <PublicRoute>
                  <FoodMenuComponent />
                </PublicRoute>
              }
            />
            <Route
              path="/BeerRoulette"
              element={
                <PublicRoute>
                  <BeerRoulette />
                </PublicRoute>
              }
            />
            <Route
              path="/login"
              element={
                <PublicRoute>
                  <Login />
                </PublicRoute>
              }
            />
            <Route
              path="/signup"
              element={
                <PublicRoute>
                  <Signup />
                </PublicRoute>
              }
            />
            <Route
              path="/guia"
              element={
                <PublicRoute>
                  <RestaurantExplorer />
                </PublicRoute>
              }
            />
            <Route
              path="/phonesignup"
              element={
                <PublicRoute>
                  <PhoneSignUp />
                </PublicRoute>
              }
            />
            <Route
              path="/welcomeaboard"
              element={
                <PublicRoute>
                  <NewUser />
                </PublicRoute>
              }
            />
            <Route
              path="/go/:slug"
              element={
                <PublicRoute>
                  <PlaceInfo />
                </PublicRoute>
              }
            />

            {/* 🔒 RUTAS PRIVADAS - REQUIEREN LOGIN */}
            <Route
              path="/home"
              element={
                <PrivateRoute>
                  <Home />
                </PrivateRoute>
              }
            />
            <Route
              path="/menudashboard"
              element={
                <PrivateRoute>
                  <Dashboard />
                </PrivateRoute>
              }
            />
            <Route
              path="/uploadmenu"
              element={
                <PrivateRoute>
                  <UploadMenuComponent />
                </PrivateRoute>
              }
            />
            <Route
              path="/producto/:documentName"
              element={
                <PrivateRoute>
                  <ProductDataFetcher />
                </PrivateRoute>
              }
            />
            <Route
              path="/productos/categoria/:id"
              element={
                <PrivateRoute>
                  <ProductosCategoria />
                </PrivateRoute>
              }
            />
            <Route
              path="/search/:input"
              element={
                <PrivateRoute>
                  <Search />
                </PrivateRoute>
              }
            />
            <Route
              path="/categorias"
              element={
                <PrivateRoute>
                  <Categorias />
                </PrivateRoute>
              }
            />
            <Route
              path="/sendNotification"
              element={
                <PrivateRoute>
                  <SendNotification />
                </PrivateRoute>
              }
            />
            <Route
              path="/MyFriends/:uid"
              element={
                <PrivateRoute>
                  <MyFriends />
                </PrivateRoute>
              }
            />
            <Route
              path="/transaction/:uid"
              element={
                <PrivateRoute>
                  <TransactionDetails />
                </PrivateRoute>
              }
            />
            <Route
              path="/wallet"
              element={
                <PrivateRoute>
                  <Wallet />
                </PrivateRoute>
              }
            />
            <Route
              path="/notauthorized"
              element={
                <PrivateRoute>
                  <NotAuthorizedScreen />
                </PrivateRoute>
              }
            />
            <Route
              path="/search"
              element={
                <PrivateRoute>
                  <SearchPanel />
                </PrivateRoute>
              }
            />
            <Route
              path="/friends"
              element={
                <PrivateRoute>
                  <SearchFriends />
                </PrivateRoute>
              }
            />
            <Route
              path="/checkout/:uid"
              element={
                <PrivateRoute>
                  <CheckOut />
                </PrivateRoute>
              }
            />

            {/* 👑 ADMIN DASHBOARD - REQUIERE PERMISO canAccessAdmin (admin, gerente, cajero) */}
            <Route
              path="/admin"
              element={
                <PrivateRoute>
                  <AdminLayout>
                    <NewAdminDashboard />
                  </AdminLayout>
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/store/new"
              element={
                <PrivateRoute>
                  <AdminLayout>
                    <StoreEditor />
                  </AdminLayout>
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/store/edit/:storeId"
              element={
                <PrivateRoute>
                  <AdminLayout>
                    <StoreEditor />
                  </AdminLayout>
                </PrivateRoute>
              }
            />

            <Route
              path="/myactivities"
              element={
                <PrivateRoute>
                  <MyActivities />
                </PrivateRoute>
              }
            />
            <Route
              path="/newactivity"
              element={
                <PrivateRoute>
                  <NewActivity />
                </PrivateRoute>
              }
            />
            <Route
              path="/memberships"
              element={
                <PrivateRoute>
                  <MembershipView />
                </PrivateRoute>
              }
            />
            <Route
              path="/stores"
              element={
                <PrivateRoute>
                  <AllStores />
                </PrivateRoute>
              }
            />
            <Route
              path="/createstore"
              element={
                <PrivateRoute>
                  <CreateStore />
                </PrivateRoute>
              }
            />
            <Route
              path="/success/:orderId"
              element={
                <PrivateRoute>
                  <Success />
                </PrivateRoute>
              }
            />
            <Route
              path="/failure"
              element={
                <PrivateRoute>
                  <Failure />
                </PrivateRoute>
              }
            />
            <Route
              path="/pending"
              element={
                <PrivateRoute>
                  <Pending />
                </PrivateRoute>
              }
            />
            <Route
              path="/friendsrequests"
              element={
                <PrivateRoute>
                  <FriendRequests />
                </PrivateRoute>
              }
            />
            <Route
              path="/user/:uid"
              element={
                <PrivateRoute>
                  <Profile />
                </PrivateRoute>
              }
            />
            <Route
              path="/addProfile"
              element={
                <PrivateRoute>
                  <UserForm />
                </PrivateRoute>
              }
            />
            <Route
              path="/profile"
              element={
                <PrivateRoute>
                  <UserMenu />
                </PrivateRoute>
              }
            />
            <Route
              path="/carrito"
              element={
                <PrivateRoute>
                  <Cart />
                </PrivateRoute>
              }
            />
            <Route
              path="/messages"
              element={
                <PrivateRoute>
                  <Inbox />
                </PrivateRoute>
              }
            />
            <Route
              path="/newcategory"
              element={
                <PrivateRoute>
                  <NewCategory />
                </PrivateRoute>
              }
            />
            <Route
              path="/feed"
              element={
                <PrivateRoute>
                  <Feed />
                </PrivateRoute>
              }
            />
            <Route
              path="/restback"
              element={
                <PrivateRoute>
                  <RestBack />
                </PrivateRoute>
              }
            />
            <Route
              path="/newplace"
              element={
                <PrivateRoute>
                  <NewPlace />
                </PrivateRoute>
              }
            />
            <Route
              path="/orders"
              element={
                <PrivateRoute>
                  <Orders />
                </PrivateRoute>
              }
            />
            <Route
              path="/qr"
              element={
                <PrivateRoute>
                  <QR />
                </PrivateRoute>
              }
            />

            {/* 404 */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </CartProvider>
      </CurrencyProvider>
    </UserAuthContextProvider>
  );
}

export default App;
